import { useEffect, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { cn } from 'utils/style-utils';
import IconRightArrow from 'crono-fe-common/icons/Icon-Right-Arrow';
import { colors } from 'crono-fe-common/theme';
import { Prospect } from 'crono-fe-common/types/prospect';
import SequenceInstanceButton from 'pages/lists/contactTable/SequenceInstanceButton';
import { ControlledPopover } from 'components/Overlay';
import {
  hasActiveSequence,
  hasStoppedSequence,
} from 'pages/lists/contactTable/sequenceSelection';
import ResumeSequenceInstance from 'pages/lists/contactTable/resumeSequenceInstance';
import StrategySelector from 'pages/lists/contactTable/strategySelector';
import useStopSequenceInstance from 'hooks/services/strategy/useStopSequenceInstance';
import useGetStrategy, {
  useInvalidateGetStrategy,
} from 'hooks/services/strategy/useGetStrategy';
import { useAnalytics } from 'context/analytics';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError } from 'crono-fe-common/utils';
import RescheduleCalendar from 'pages/taskBoard/rescheduleCalendar';
import SequenceInstanceDisplay from 'pages/taskBoard/sequenceInstance';
import usePatchTask from 'hooks/services/task/usePatchTask';
import styled from '@emotion/styled';
import { StrategyCopyConstants } from 'crono-fe-common/constants/copyConstants/strategyCopyConstants';
import { useAuth } from 'context/auth';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useNonOwnedSequenceActionGuard from 'hooks/strategy/useNonOwnedSequenceActionGuard';

interface IProps {
  prospect: Prospect;
}

const SequenceHandlingButton = ({ prospect }: IProps) => {
  const { user } = useAuth();
  const { isManager } = useRoleCapabilities();
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();

  const {
    mutate: patchTask,
    isLoading: patchLoading,
    isSuccess: patchSuccess,
    error: errorPatchTask,
    reset: resetPatchTask,
  } = usePatchTask();
  const analytics = useAnalytics();
  const {
    mutateAsync: stopSequenceInstanceAsync,
    error: errorStopSequenceInstance,
  } = useStopSequenceInstance();

  const [showRescheduleTask, setShowRescheduleTask] = useState<{
    index: number;
    initialDate: Date;
    //A manager rescheduling a step of a sequence owned by another user
    onBehalf: boolean;
  } | null>(null);

  //A contact whose sequence can no longer be stopped nor resumed is offered a new
  //one instead: a finished sequence, or one left on the company the contact has
  //left, cannot be reopened
  const hasSequenceToHandle =
    hasActiveSequence(prospect) || hasStoppedSequence(prospect);

  useEffect(() => {
    if (patchSuccess) setShowRescheduleTask(null);
  }, [patchSuccess]);

  const handleOpenDateCalendar = (task: TaskTodo) => {
    setShowRescheduleTask({
      index: task.id,
      initialDate: task.activityDate,
      onBehalf: !!task.userId && task.userId !== user?.id,
    });
  };
  const handleChangeTaskDate = (id: number, newDate: Date) => {
    patchTask({
      id: id,
      activityDate: newDate,
      onBehalf: showRescheduleTask?.onBehalf,
    });

    if (analytics) {
      analytics.track('patch-task', {
        type: 'reschedule',
      });
    }
  };
  const handleChangeDate = (newDate: Date) => {
    handleChangeTaskDate(showRescheduleTask!.index, newDate);
  };

  const [
    selectedSequenceInstanceToDisplay,
    setSelectedSequenceInstanceToDisplay,
  ] = useState<{ sequenceInstanceId: number; prospect: Prospect } | null>(null);
  const handleShowSequenceInstance = (
    sequenceInstanceId: number,
    prospect: Prospect,
  ) => {
    setSelectedSequenceInstanceToDisplay({ sequenceInstanceId, prospect });
  };

  const [open, setOpen] = useState(false);

  const { data: strategy } = useGetStrategy(
    prospect &&
      prospect.sequenceInstance &&
      !prospect.sequenceInstance.strategyName
      ? prospect.sequenceInstance?.strategyId
      : undefined,
  );

  const strategyName =
    prospect.sequenceInstance?.strategyName ?? strategy?.data?.data.name ?? '';

  const handleTooltipClose = () => {
    if (open) {
      setOpen(false);
    }
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const invalidateGetStrategy = useInvalidateGetStrategy();

  //Managers can act on sequences of contacts they don't own, without taking ownership
  const canActOnSequence = !!prospect.owned || isManager;

  const stopSequence = async (
    prospectId: string,
    sequenceInstanceId: number,
    strategyId: number,
  ) => {
    await stopSequenceInstanceAsync({
      sequenceInstanceId,
      prospectId,
      strategyId,
      onBehalf: !prospect.owned,
    });

    invalidateGetStrategy({ strategyId });
    if (analytics) {
      await analytics.track('stop-sequence', {});
    }
  };

  const handleStopSequence = () => {
    const sequenceInstance = prospect.sequenceInstance;
    if (!sequenceInstance || !canActOnSequence) return;

    const stopCurrentSequence = () =>
      stopSequence(
        prospect.objectId,
        sequenceInstance.id,
        sequenceInstance.strategyId,
      );

    if (!prospect.owned) {
      openNonOwnedSequenceActionModal('stop', stopCurrentSequence);
    } else {
      stopCurrentSequence();
    }
  };

  useConditionalSnackBar([
    {
      condition: !!errorStopSequenceInstance,
      message:
        getError(errorStopSequenceInstance) ??
        'Error stopping prospect sequence',
      severity: 'error',
    },
    {
      condition:
        !!errorPatchTask &&
        !(
          errorPatchTask.response?.data?.errors?.at(0)?.code ===
          'TaskErrorInconsistentReschedule'
        ),
      message: getError(errorPatchTask) ?? 'Error updating task',
      severity: 'error',
    },
  ]);

  return (
    <SequenceHandlingButtonWrapper>
      {selectedSequenceInstanceToDisplay && (
        <SequenceInstanceDisplay
          sequenceInstanceId={
            selectedSequenceInstanceToDisplay.sequenceInstanceId
          }
          prospect={selectedSequenceInstanceToDisplay.prospect}
          onClose={() => setSelectedSequenceInstanceToDisplay(null)}
          handleOpenDateCalendar={handleOpenDateCalendar}
        />
      )}
      {showRescheduleTask && (
        <RescheduleCalendar
          isRescheduleLoading={patchLoading}
          onClose={() => {
            setShowRescheduleTask(null);
            resetPatchTask();
          }}
          onConfirm={handleChangeDate}
          initialDate={showRescheduleTask.initialDate}
          error={
            errorPatchTask?.response?.data?.errors?.at(0)?.code ===
            'TaskErrorInconsistentReschedule'
              ? errorPatchTask?.response?.data?.errors?.at(0)?.message
              : undefined
          }
        />
      )}
      {!prospect.optedOut &&
        (prospect.sequenceInstance && hasSequenceToHandle ? (
          prospect.sequenceInstance.active ? (
            /*If there is an active sequenceInstance */
            <Tooltip
              arrow
              title={
                !canActOnSequence
                  ? StrategyCopyConstants.notOwnedAndEnrolledMessage(
                      prospect.user,
                    )
                  : undefined
              }
              placement="top-start"
              enterDelay={400}
              enterNextDelay={400}
            >
              <div
                className={cn('pause-sequence-container', {
                  disabled: !canActOnSequence,
                })}
              >
                <div className="pause-sequence-handling-container">
                  <div
                    className="sequence-button-wrapper"
                    onClick={handleStopSequence}
                  >
                    <Tooltip
                      arrow
                      title={
                        !canActOnSequence
                          ? null
                          : StrategyCopyConstants.STOP_INSTANCE_TEXT
                      }
                      placement="top"
                      enterDelay={400}
                      enterNextDelay={400}
                    >
                      <SequenceInstanceButton
                        action="stop"
                        size="small"
                        disableTooltip
                        whiteHover
                        disabled={!canActOnSequence}
                      />
                    </Tooltip>
                  </div>
                </div>
                <div
                  className={cn('strategy-name-container', {
                    'disabled-strategy-name-container': !canActOnSequence,
                  })}
                  onClick={() => {
                    if (
                      prospect.sequenceInstance?.id &&
                      prospect.sequenceInstance?.active &&
                      canActOnSequence
                    )
                      handleShowSequenceInstance(
                        prospect.sequenceInstance.id,
                        prospect,
                      );
                  }}
                >
                  <Typography
                    fontSize={'14px'}
                    className="sequence-label"
                    fontWeight={400}
                    color={colors.grey11}
                    flexShrink={0}
                  >
                    Sequence:{' '}
                  </Typography>

                  <Typography
                    fontSize={'14px'}
                    className="sequence-label"
                    fontWeight={500}
                    color={colors.mainDark}
                  >
                    {strategyName}
                  </Typography>

                  <div className="steps-container">
                    <Typography fontSize={'14px'} fontWeight={500}>
                      {' '}
                      {prospect.sequenceInstance?.actualStep}
                    </Typography>
                    <Typography
                      fontSize={'14px'}
                      fontWeight={500}
                      color={colors.grey11}
                    >
                      /{prospect.sequenceInstance?.numberOfSteps}
                    </Typography>
                  </div>

                  <div className="open-sequence-instance-arrow">
                    <IconRightArrow color={colors.mainDark} />
                  </div>
                </div>
              </div>
            </Tooltip>
          ) : (
            /*If there is an inactive sequenceInstance */
            <>
              <div
                className={cn('resume-sequence-handling-container', {
                  disabled: !canActOnSequence,
                })}
              >
                <ControlledPopover
                  open={open}
                  onClose={handleTooltipClose}
                  placement="bottom-start"
                  className="rounded-2xl"
                  content={
                    <ResumeSequenceInstance
                      prospect={prospect}
                      //This has to be passed in order to set the state to false after the action performed
                      //without this the tooltip would open again after the next click on the pause button
                      setOpenTooltip={setOpen}
                      addToStrategyOrientation={'left'}
                    />
                  }
                >
                  <div
                    className="sequence-button-wrapper"
                    onClick={handleTooltipOpen}
                  >
                    <Tooltip
                      arrow
                      title={
                        !canActOnSequence
                          ? prospect.sequenceInstance?.userId === user?.id
                            ? StrategyCopyConstants.NOT_ALLOWED_TO_ENROLL_DUE_TO_OWNERSHIP
                            : StrategyCopyConstants.ALREADY_IN_A_STRATEGY
                          : StrategyCopyConstants.RESUME_INSTANCE_TEXT
                      }
                      placement="top"
                      enterDelay={400}
                      enterNextDelay={400}
                    >
                      <SequenceInstanceButton
                        action="start"
                        size="small"
                        whiteHover
                        disableTooltip
                        disabled={!canActOnSequence}
                      />
                    </Tooltip>
                  </div>
                </ControlledPopover>
              </div>
              <Tooltip
                arrow
                title={StrategyCopyConstants.STOPPED_INSTANCE_TEXT}
                placement="top-start"
                enterDelay={400}
                enterNextDelay={400}
              >
                <div
                  className={
                    'strategy-name-container disabled-strategy-name-container'
                  }
                >
                  <Typography
                    fontSize={'14px'}
                    className="sequence-label"
                    fontWeight={400}
                    color={colors.grey11}
                    flexShrink={0}
                  >
                    Sequence:{' '}
                  </Typography>
                  <Typography
                    fontSize={'14px'}
                    className="sequence-label"
                    fontWeight={500}
                    color={colors.grey2}
                  >
                    {strategyName}
                  </Typography>
                  <Typography
                    fontSize={'14px'}
                    fontWeight={500}
                    color={colors.grey2}
                  >
                    {prospect.sequenceInstance?.actualStep}/
                    {prospect.sequenceInstance?.numberOfSteps}
                  </Typography>
                </div>
              </Tooltip>
            </>
          )
        ) : canActOnSequence ? (
          /*If there is no sequenceInstance */
          <div className={'add-sequence-handling-container'}>
            <ControlledPopover
              open={open}
              onClose={handleTooltipClose}
              placement="bottom-start"
              className="rounded-2xl"
              content={
                <StrategySelector
                  prospect={prospect}
                  setOpenTooltip={setOpen}
                />
              }
            >
              <div
                className="sequence-button-wrapper"
                style={{ width: 'fit-content', cursor: 'pointer' }}
                onClick={handleTooltipOpen}
              >
                <SequenceInstanceButton
                  action="add"
                  size="small"
                  iconFill={colors.darkGreen}
                  disableHover
                  disableTooltip
                />
                <Typography
                  className="sequence-label"
                  fontSize={'14px'}
                  fontWeight={500}
                  lineHeight={'18px'}
                  color={colors.white}
                >
                  {StrategyCopyConstants.ADD_TO_STRATEGY_TEXT}
                </Typography>
              </div>
            </ControlledPopover>
          </div>
        ) : null)}
    </SequenceHandlingButtonWrapper>
  );
};

export default SequenceHandlingButton;

const SequenceHandlingButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  .resume-sequence-handling-container,
  .add-sequence-handling-container,
  .pause-sequence-handling-container {
    border-radius: 9999px;
  }
  .add-sequence-handling-container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 12px;
    border-radius: 24px;
    padding: 4px 8px 4px 4px;
    background: ${colors.primary};
    &:hover {
      background: ${colors.primaryDark};
    }
  }
  .resume-sequence-handling-container:hover {
    background-color: ${colors.callScheduledLight};
  }
  .resume-sequence-handling-container:hover > div > p {
    color: ${colors.callScheduledHover} !important;
  }
  .pause-sequence-container {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px 4px 4px;
    border-radius: 99px;
    background-color: ${colors.orangeLight};
    &.disabled {
      background-color: ${colors.grey6};
      :hover {
        cursor: not-allowed;
      }
    }
    &:not(.disabled):hover {
      background-color: ${colors.orangeLightHover};
    }
  }
  .sequence-button-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sequence-label {
    max-width: 170px;
    width: fit-content;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
  }
  .expand-arrow-icon {
    cursor: pointer;
    min-width: 34px;
    min-height: 34px;
    padding: 3px;
    border: 1px solid ${colors.grey4};
    border-radius: 999px;
    &:hover {
      background: ${colors.grey6};
    }
  }

  .expand-arrow-icon:hover > svg > path {
    stroke: ${colors.grey1};
  }
  .steps-container {
    display: flex;
    align-items: center;
    min-width: 32px;
  }
  .open-sequence-instance-arrow {
    display: none;
    height: fit-content;
    align-items: center;
    justify-content: center;
    min-width: 32px;
  }
  .strategy-name-container {
    display: flex;
    margin-left: 8px;
    cursor: pointer;
    align-items: center;
    gap: 4px;
    &:hover {
      transition: all 0.2s;
      /* & > * {
        color: ${colors.primaryDark};
      } */
      .steps-container {
        display: none;
      }
      .open-sequence-instance-arrow {
        display: flex;
      }
    }
  }

  .disabled-strategy-name-container:hover {
    cursor: not-allowed;
    & > * {
      color: ${colors.grey11};
    }
  }
`;
